/**
 * Clamp AI output destined for a SHORT field (email subject / title) to a single
 * subject-shaped line.
 *
 * The model is instructed to return something "under 60 characters", but that's
 * a SOFT constraint — with sampling it can occasionally return a greeting plus a
 * multi-paragraph body, which would dump a whole email into the subject box
 * ("improve subject → full body in subject"). The generate/improve helpers apply
 * the result verbatim, so this is the hard guard:
 *   - take the first meaningful line (drops a body that starts on later lines),
 *   - strip a leading "Subject:" / "Title:" label and wrapping quotes,
 *   - clamp a run-on first line to its first sentence (then a word boundary).
 *
 * Pinned by ai-text.test.ts.
 */
export function toSubjectLine(text: string): string {
  const cleaned = (text ?? "").replace(/\r/g, "")
  const firstLine =
    cleaned
      .split("\n")
      .map((l) => l.trim())
      .find((l) => l.length > 0) ?? cleaned.trim()

  let line = firstLine
    .replace(/^#{1,6}\s+/, "") // markdown heading "# " / "## "
    .replace(/^(subject|title)\s*:\s*/i, "") // "Subject:" / "Title:" label
    .replace(/^[\s"'*>_]+/, "") // leading quotes / bold-asterisks / blockquote / underscore
    .replace(/[\s"'*_]+$/, "") // trailing decoration
    .trim()

  if (line.length > 140) {
    const firstSentence = (line.split(/(?<=[.!?])\s+/)[0] ?? line).trim()
    line =
      firstSentence.length > 0 && firstSentence.length <= 140
        ? firstSentence
        : line.slice(0, 140).replace(/\s+\S*$/, "").trim()
  }

  return line
}
